import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Phone, Mail, MessageCircle, Loader2 } from 'lucide-react';
import { useContactExchange } from '@/hooks/useContactExchange';
import { useTranslation } from 'react-i18next';

interface ContactExchangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  matchId: string;
  requestId?: string;
  isApproving?: boolean;
}

export const ContactExchangeModal = ({ 
  isOpen, 
  onClose, 
  matchId, 
  requestId,
  isApproving = false
}: ContactExchangeModalProps) => {
  const { t } = useTranslation();
  const { createRequest, approveRequest, rejectRequest } = useContactExchange(matchId);
  const [contactData, setContactData] = useState({
    email: '',
    phone: '',
    whatsapp: '',
    notes: ''
  });

  const handleCreate = async () => {
    await createRequest.mutateAsync(matchId);
    onClose();
  };

  const handleApprove = async () => {
    if (!requestId) return;
    
    await approveRequest.mutateAsync({
      request_id: requestId,
      contact_data: contactData
    });
    onClose();
  };

  const handleReject = async () => {
    if (!requestId) return;
    
    await rejectRequest.mutateAsync(requestId);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>
            {isApproving ? t('contactExchange.modal.shareTitle') : t('contactExchange.modal.requestTitle')}
          </DialogTitle>
          <DialogDescription>
            {isApproving 
              ? t('contactExchange.modal.shareDescription')
              : t('contactExchange.modal.requestDescription')
            }
          </DialogDescription>
        </DialogHeader>

        {isApproving ? (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                {t('contactExchange.modal.email')}
              </Label>
              <Input
                id="email"
                type="email"
                placeholder={t('contactExchange.modal.emailPlaceholder')}
                value={contactData.email}
                onChange={(e) => setContactData(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                {t('contactExchange.modal.phoneOptional')}
              </Label>
              <Input
                id="phone"
                type="tel"
                placeholder={t('contactExchange.modal.phonePlaceholder')}
                value={contactData.phone}
                onChange={(e) => setContactData(prev => ({ ...prev, phone: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="whatsapp" className="flex items-center gap-2">
                <MessageCircle className="h-4 w-4" />
                {t('contactExchange.modal.whatsappOptional')}
              </Label>
              <Input
                id="whatsapp"
                type="tel"
                placeholder={t('contactExchange.modal.whatsappPlaceholder')}
                value={contactData.whatsapp}
                onChange={(e) => setContactData(prev => ({ ...prev, whatsapp: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">{t('contactExchange.modal.notesOptional')}</Label>
              <Textarea
                id="notes"
                placeholder={t('contactExchange.modal.notesPlaceholder')}
                value={contactData.notes}
                onChange={(e) => setContactData(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={handleReject}
                disabled={rejectRequest.isPending}
                className="flex-1"
              >
                {t('contactExchange.modal.decline')}
              </Button>
              <Button
                onClick={handleApprove}
                disabled={!contactData.email || approveRequest.isPending}
                className="flex-1 bg-gradient-to-r from-pink-500 to-purple-600"
              >
                {approveRequest.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t('contactExchange.modal.shareContact')
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              {t('contactExchange.modal.requestExplanation')}
            </p>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleCreate}
                disabled={createRequest.isPending}
                className="flex-1 bg-gradient-to-r from-pink-500 to-purple-600"
              >
                {createRequest.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  t('contactExchange.modal.sendRequest')
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
